import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Award, Users, Calendar, BookOpen } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';

const AnimatedCounter = ({ end, duration = 2000, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [isVisible, end, duration]);

  return (
    <span ref={ref}>
      {prefix}{count.toLocaleString('pt-BR')}{suffix}
    </span>
  );
};

const ImpactCounters = () => {
  const { impactData } = useLanguage();
  
  const icons = [Award, Users, Calendar, BookOpen];

  const accentColors = {
    purple: 'var(--accent-purple-200)',
    blue: 'var(--accent-blue-200)',
    pink: 'var(--accent-pink-200)',
    orange: 'var(--accent-orange-200)'
  };

  return (
    <section 
      className="py-16 md:py-24"
      style={{ background: 'var(--bg-section)' }}
    >
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="heading-1 mb-2">{impactData.title}</h2>
          <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
            {impactData.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {impactData.stats.map((stat, index) => {
            const IconComponent = icons[index % icons.length];
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="voice-card text-center hover-lift"
                style={{ background: accentColors[stat.accent] }}
              >
                <div 
                  className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
                  style={{ background: 'rgba(255, 255, 255, 0.5)' }}
                >
                  <IconComponent size={24} style={{ color: 'var(--text-primary)' }} />
                </div>
                
                <div className="heading-hero mb-1" style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)' }}>
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} duration={2500} />
                </div>
                
                <p className="heading-3 mb-1" style={{ fontSize: '0.9rem' }}>
                  {stat.label}
                </p>
                
                <p className="caption" style={{ color: 'var(--text-muted)' }}>
                  {stat.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ImpactCounters;
